import { useEffect } from "react";

import { getCornerstone } from "../utils/dicomLoader";
import { SHARED_RENDERING_ENGINE_ID } from "../utils/renderingEngine";
import { logger } from "@/utils/logger";

/**
 * Slice-dependent camera sync — keeps the axial and MIP viewports fully
 * mirrored bidirectionally: slice (focalPoint along z), pan (focalPoint
 * in-plane), and zoom (parallelScale).
 *
 * Both viewports share the same volume and are coplanar axial views, so a
 * full camera mirror is correct — pan/zoom either one and the other follows.
 * Sync is bidirectional: scrolling on the MIP also drives the axial.
 *
 * Loop prevention: when we set the camera on the "other" viewport, that
 * viewport's CAMERA_MODIFIED listener fires too. The approx-equality guard
 * sees both cameras already agree and returns early, breaking the loop in
 * one step.
 *
 * Wire-up is gated on both viewports having actors attached (i.e. their
 * volumes loaded). Without that gate, the MIP's late volume-init
 * CAMERA_MODIFIED event could mirror back to the axial and clobber the
 * user's startup state. Polling continues until both are ready or we hit
 * a 5s budget.
 */
export const useSliceDependentSync = (
  axialViewportId: string | null,
  mipViewportId: string | null,
) => {
  useEffect(() => {
    if (!axialViewportId || !mipViewportId) return;

    let cs: ReturnType<typeof getCornerstone>;
    try {
      cs = getCornerstone();
    } catch {
      return;
    }

    let cleanupListeners: (() => void) | null = null;

    const tryWire = (): boolean => {
      const engine = cs.getRenderingEngine(SHARED_RENDERING_ENGINE_ID);
      if (!engine) return false;

      const axialVp = engine.getViewport(axialViewportId) as any;
      const mipVp = engine.getViewport(mipViewportId) as any;
      if (!axialVp || !mipVp) return false;
      if (!axialVp.element || !mipVp.element) return false;
      // Both viewports must have a volume attached. Wiring before either
      // has actors lets the late-loading viewport's init CAMERA_MODIFIED
      // event mirror onto the already-set viewport and reset it.
      if (!axialVp.getActors?.()?.length) return false;
      if (!mipVp.getActors?.()?.length) return false;

      const camsApproxEqual = (a: any, b: any): boolean => {
        const eq = (x: number, y: number) => Math.abs(x - y) < 1e-6;
        return (
          eq(a.focalPoint[0], b.focalPoint[0]) &&
          eq(a.focalPoint[1], b.focalPoint[1]) &&
          eq(a.focalPoint[2], b.focalPoint[2]) &&
          eq(a.position[0], b.position[0]) &&
          eq(a.position[1], b.position[1]) &&
          eq(a.position[2], b.position[2]) &&
          eq(a.parallelScale, b.parallelScale)
        );
      };

      const mirror = (src: any, dst: any) => {
        try {
          const srcCam = src.getCamera?.();
          if (!srcCam?.focalPoint || !srcCam?.position) return;
          const dstCam = dst.getCamera?.();
          if (!dstCam) return;
          if (camsApproxEqual(srcCam, dstCam)) return;
          dst.setCamera({
            ...dstCam,
            focalPoint: [...srcCam.focalPoint] as [number, number, number],
            position: [...srcCam.position] as [number, number, number],
            viewUp: [...srcCam.viewUp] as [number, number, number],
            parallelScale: srcCam.parallelScale,
          });
          dst.render?.();
        } catch (e) {
          logger.debug("[useSliceDependentSync] mirror failed:", e);
        }
      };

      const onAxialCameraModified = () => mirror(axialVp, mipVp);
      const onMipCameraModified = () => mirror(mipVp, axialVp);

      const axialElement = axialVp.element as HTMLElement;
      const mipElement = mipVp.element as HTMLElement;
      axialElement.addEventListener("CORNERSTONE_CAMERA_MODIFIED" as any, onAxialCameraModified);
      mipElement.addEventListener("CORNERSTONE_CAMERA_MODIFIED" as any, onMipCameraModified);

      cleanupListeners = () => {
        axialElement.removeEventListener(
          "CORNERSTONE_CAMERA_MODIFIED" as any,
          onAxialCameraModified,
        );
        mipElement.removeEventListener("CORNERSTONE_CAMERA_MODIFIED" as any, onMipCameraModified);
      };

      // Seed once now that both viewports have volumes. Axial wins on first
      // wire — it's the annotating viewport, and we've already gated above on
      // the MIP being ready, so this seed is safe.
      onAxialCameraModified();
      return true;
    };

    if (tryWire()) return cleanupListeners ?? undefined;

    // Race window: VolumeViewports may not be created yet, or their volumes
    // may still be loading. Poll up to 5s.
    let attempts = 0;
    const interval = window.setInterval(() => {
      attempts += 1;
      if (tryWire()) {
        window.clearInterval(interval);
        return;
      }
      if (attempts >= 20) {
        window.clearInterval(interval);
        logger.debug("[useSliceDependentSync] gave up waiting for viewports");
      }
    }, 250);

    return () => {
      window.clearInterval(interval);
      cleanupListeners?.();
    };
  }, [axialViewportId, mipViewportId]);
};
